import { DeleteOutline } from '@mui/icons-material';
import { Button, Stack, Tab, Tabs } from '@mui/material';
import { Box } from '@mui/system';
import { UseQueryResult } from '@tanstack/react-query';
import { useMapsGoogleConfigurationQuery } from 'api/queries/config';
import { useDeleteOperatorsOrdersMutation, useOperatorsOrdersQuery } from 'api/queries/operators';
import { useOrdersFilesQuery } from 'api/queries/orders';
import { PricingTransparency } from 'components/modals/PricingTransparencyModal/PricingTransparencyModal';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  colors,
  ConfirmModal,
  FileTypeEnum,
  LegSchemaType,
  OrderDetails,
  PageLoader,
  PriceDisplay,
  RideDetails,
  RideDtoSchemaType,
  RideOfferType,
} from 'remote/global';
import OrderDetailsEditCarrier from '@/components/modals/OrderModal/OrderDetailsEditCarrier';
import BookingEditExtrasForm from './BookingEditExtrasForm';
import BookingEditGroupLeadForm from './BookingEditGroupLeadForm';
import BookingEditRideJourneyForm from './BookingEditRideJourneyForm';

// Specify the view type
enum FormViewType {
  ORDER = 'order',
  RIDE = 'ride',
}

enum ActiveTabType {
  ORDER_OVERVIEW = 'order-overview',
  RIDE_OVERVIEW = 'ride-overview',
  RIDE_JOURNEY = 'ride-journey',
  RIDE_GROUP_LEAD = 'ride-group-lead',
  RIDE_EXTRAS = 'ride-extras',
  RIDE_PRICING = 'ride-pricing',
  DELETE_BOOKING = 'delete-booking',
}

type Props = {
  isOrderEditAvailable: boolean;
  rideId?: number;
  orderQuery: UseQueryResult<any>;
  onClose: () => void;
  viewType: FormViewType; // Determine the view
};

function BookingExtrasGate({
  bookingNumber,
  ride,
  isOrderEditAvailable,
}: {
  bookingNumber: string;
  isOrderEditAvailable: boolean;
  ride: RideDtoSchemaType;
}) {
  const { data: order, isSuccess: isSuccessOrder } = useOperatorsOrdersQuery(bookingNumber);
  const { data: orderFiles, isSuccess: isSuccessFiles } = useOrdersFilesQuery(bookingNumber);

  if (!isSuccessOrder || !isSuccessFiles)
    return (
      <Box sx={{ my: '3rem' }}>
        <PageLoader />
      </Box>
    );

  const rideDetailsFiles = orderFiles.filter((f) => f.fileType === FileTypeEnum.RIDE_DETAILS);
  const signFiles = orderFiles.filter((f) => f.fileType === FileTypeEnum.RIDE_SIGN);

  return (
    <BookingEditExtrasForm
      isOrderEditAvailable={isOrderEditAvailable}
      bookingNumber={bookingNumber}
      defaultValues={{ ...(order as any), signFiles, rideDetailsFiles }}
      ride={ride}
    />
  );
}

function getUniqueVehicles(rides: any[]) {
  const uniqueMap = new Map();
  rides.forEach((ride) => {
    if (ride?.vehicle?.id && !uniqueMap.has(ride.vehicle.id)) {
      uniqueMap.set(ride.vehicle.id, ride);
    }
  });
  return Array.from(uniqueMap.values());
}

function BookingEditForm({ orderQuery, rideId, isOrderEditAvailable, onClose, viewType }: Props) {
  const { data: googleConfig } = useMapsGoogleConfigurationQuery();
  const { t } = useTranslation();
  // Set initial tab based on viewType
  const [activeTab, setActiveTab] = useState<ActiveTabType>(() =>
    viewType === FormViewType.ORDER ? ActiveTabType.ORDER_OVERVIEW : ActiveTabType.RIDE_OVERVIEW
  );

  useEffect(() => {
    // Reset tab when viewType changes
    setActiveTab(
      viewType === FormViewType.ORDER ? ActiveTabType.ORDER_OVERVIEW : ActiveTabType.RIDE_OVERVIEW
    );
  }, [viewType]);

  const order = orderQuery.data;
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const deleteOrderMutation = useDeleteOperatorsOrdersMutation(order?.bookingNumber);

  const [showRideDetails, setShowRideDetails] = useState(false); // Only relevant for ORDER view
  const [selectedRideId, setSelectedRideId] = useState<number | undefined>(rideId);

  // Determine the selected ride based on viewType
  const selectedRide =
    viewType === FormViewType.RIDE
      ? order?.rides?.find((ride: any) => ride.id === rideId)
      : order?.rides?.find((ride: any) => ride.id === selectedRideId);

  const isPricingAvailable = order.type !== RideOfferType.CORPORATE_CHARTER;

  const handleDeleteBooking = async () => {
    try {
      await deleteOrderMutation.mutateAsync();
      setIsConfirmDeleteModalOpen(false);
      onClose();
    } catch (e) {
      // TODO: handle error
    }
  };

  if (!order || (viewType === FormViewType.RIDE && !selectedRide)) return null;

  return (
    <>
      <Box
        component='header'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          p: ['1rem 1rem 0 1rem'],
          backgroundColor: 'rgba(6, 151, 199, 0.05)',
        }}
      >
        <Tabs
          sx={{ pb: '1rem' }}
          value={activeTab}
          onChange={(_, tab) => setActiveTab(tab)}
          variant='scrollable'
        >
          {viewType === FormViewType.ORDER ? (
            <Tab label={t('modals.editOrder.tabs.overview')} value={ActiveTabType.ORDER_OVERVIEW} />
          ) : (
            <Tab label={t('modals.editOrder.tabs.overview')} value={ActiveTabType.RIDE_OVERVIEW} />
          )}

          <Tab label={t('modals.editOrder.tabs.ride')} value={ActiveTabType.RIDE_JOURNEY} />
          <Tab label={t('modals.editOrder.tabs.groupLead')} value={ActiveTabType.RIDE_GROUP_LEAD} />
          <Tab label={t('modals.editOrder.tabs.extras')} value={ActiveTabType.RIDE_EXTRAS} />
          {isPricingAvailable && (
            <Tab label={t('modals.editOrder.tabs.pricing')} value={ActiveTabType.RIDE_PRICING} />
          )}
          <Button
            sx={{ ml: 'auto' }}
            disabled={!isOrderEditAvailable}
            size='small'
            variant='text'
            onClick={() => setIsConfirmDeleteModalOpen(true)}
          >
            <DeleteOutline />{' '}
          </Button>
        </Tabs>
      </Box>

      {/* Ride selection for Order View */}
      {viewType === FormViewType.ORDER &&
        [
          ActiveTabType.ORDER_OVERVIEW,
          ActiveTabType.RIDE_JOURNEY,
          ActiveTabType.RIDE_PRICING,
        ].includes(activeTab) &&
        order.rides.length > 1 && (
          <Stack direction='row' gap={1} px={2} pt={2} alignItems='center' flexWrap='wrap'>
            {activeTab === ActiveTabType.ORDER_OVERVIEW && (
              <Button
                variant={showRideDetails ? 'outlined' : 'contained'}
                size='small'
                onClick={() => setShowRideDetails(false)}
                sx={{
                  height: '26px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  border: `1px solid ${colors.uiBlue600} !important`,
                }}
              >
                All rides
              </Button>
            )}

            {order.rides.map((ride: any) => (
              <Button
                key={ride.rideNumber}
                onClick={() => {
                  setSelectedRideId(ride.id);
                  if (activeTab === ActiveTabType.ORDER_OVERVIEW) {
                    setShowRideDetails(true);
                  }
                }}
                size='small'
                variant={selectedRideId === ride.id && showRideDetails ? 'contained' : 'outlined'}
                color='primary'
                sx={{
                  height: '26px',
                  borderRadius: '6px',
                  border: `1px solid ${colors.uiBlue600} !important`,
                  fontSize: '12px',
                }}
              >
                {t('terms.attributes.ride.rideNo', { rideNo: ride.rideNumber })}
                {order.rides.some((r: any) => r.groupNumber === 2) && (
                  <> · {t('terms.attributes.ride.groupNo', { groupNo: ride.groupNumber })}</>
                )}
              </Button>
            ))}
          </Stack>
        )}

      <Box sx={{ width: '100%', p: '1rem' }}>
        {activeTab === ActiveTabType.ORDER_OVERVIEW && viewType === FormViewType.ORDER && (
          <Box sx={{ border: `1px solid ${colors.gray4}`, borderRadius: '1rem', p: 2 }}>
            {!showRideDetails && <OrderDetails orderQuery={orderQuery} />}

            {showRideDetails ? (
              <Box>
                <RideDetails
                  orderQuery={orderQuery}
                  rideId={selectedRideId!}
                  endSlot={
                    <OrderDetailsEditCarrier
                      bookingNumber={order.bookingNumber}
                      rideId={selectedRideId!}
                      showPassengerCount={false}
                    />
                  }
                />
              </Box>
            ) : (
              (() => {
                const hasMultipleRides = order.rides.length > 1;
                const uniqueVehicleRides = getUniqueVehicles(order.rides);
                const hasMultipleUniqueVehicles = uniqueVehicleRides.length > 1;

                if (hasMultipleRides) {
                  return uniqueVehicleRides.map((ride: any, index: number) => (
                    <Box
                      key={ride.id}
                      sx={{ mt: 2, borderTop: `1px solid ${colors.gray4}`, pt: 2 }}
                    >
                      <OrderDetailsEditCarrier
                        bookingNumber={order.bookingNumber}
                        rideId={ride.id}
                        showPassengerCount
                        vehicleLabel={
                          hasMultipleUniqueVehicles
                            ? `${t('FormFields.vehicleId.label')} ${index + 1}`
                            : undefined
                        }
                      />
                    </Box>
                  ));
                }
                return order.rides.map((ride: any) => (
                  <Box key={ride.id} sx={{ mt: 2, borderTop: `1px solid ${colors.gray4}`, pt: 2 }}>
                    <OrderDetailsEditCarrier
                      bookingNumber={order.bookingNumber}
                      rideId={ride.id}
                      showPassengerCount
                    />
                  </Box>
                ));
              })()
            )}
          </Box>
        )}

        {activeTab === ActiveTabType.RIDE_OVERVIEW && viewType === FormViewType.RIDE && (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: '26px',
                paddingBottom: '16px',
              }}
            >
              <Box sx={{ fontSize: '16px', fontWeight: 600, color: colors.uiBlue600 }}>
                {t('modals.editOrder.ridePrice')}
              </Box>
              <Box
                sx={{
                  transform: 'scale(0.85)',
                  transformOrigin: 'right center',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <PriceDisplay
                  priceExclVat={selectedRide.operatorPriceExclVat}
                  priceInclVat={selectedRide.operatorPriceInclVat}
                  unadjustedPriceExclVat={0}
                  unadjustedPriceInclVat={0}
                  currency={order.currency}
                  sx={{ gap: 0 }}
                />
              </Box>
            </Box>
            <Box sx={{ border: `1px solid ${colors.gray4}`, borderRadius: '1rem' }}>
              <RideDetails
                orderQuery={orderQuery}
                rideId={selectedRide.id}
                endSlot={
                  <OrderDetailsEditCarrier
                    bookingNumber={order.bookingNumber}
                    rideId={selectedRide.id}
                    showPassengerCount={false}
                  />
                }
              />
            </Box>
          </Box>
        )}

        {activeTab === ActiveTabType.RIDE_JOURNEY && selectedRide && (
          <BookingEditRideJourneyForm
            isOrderEditAvailable={isOrderEditAvailable}
            defaultValues={{
              legs: (selectedRide.legs as unknown as LegSchemaType[]) ?? [],
            }}
            rideId={selectedRide.id}
            googleConfig={googleConfig!}
          />
        )}
        {activeTab === ActiveTabType.RIDE_GROUP_LEAD && (
          <BookingEditGroupLeadForm
            isOrderEditAvailable={isOrderEditAvailable}
            bookingNumber={order.bookingNumber}
            defaultValues={{
              groupLead: order.groupLead,
            }}
          />
        )}
        {activeTab === ActiveTabType.RIDE_EXTRAS && selectedRide && (
          <BookingExtrasGate
            isOrderEditAvailable={isOrderEditAvailable}
            bookingNumber={order.bookingNumber}
            ride={selectedRide}
          />
        )}
        {isPricingAvailable && activeTab === ActiveTabType.RIDE_PRICING && selectedRide && (
          <PricingTransparency rideId={selectedRide.id} />
        )}
      </Box>
      <ConfirmModal
        open={isConfirmDeleteModalOpen}
        setOpen={setIsConfirmDeleteModalOpen}
        onClose={() => setIsConfirmDeleteModalOpen(false)}
        onConfirm={handleDeleteBooking}
        title={t('OrderView.cancel.title').toString()}
        description={t('OrderView.cancel.subtitle').toString()}
      />
    </>
  );
}

export { BookingEditForm, FormViewType };
